import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Grid2x2, Box, Plus, Trash2 } from "lucide-react";
import { BED_STATUS, BED_TYPE, ROOM_TYPE, BRAND, errMsg } from "./constants";
import { BedSheet } from "./BedSheet";

function Legend({ counts }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="bed-legend">
      {Object.entries(BED_STATUS).map(([k, s]) => (
        <span key={k} data-testid={`legend-${k}`} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold bg-white" style={{ borderColor: BRAND.line }}>
          <span className={`w-2.5 h-2.5 rounded-full ${s.blink ? "bed-blink" : ""}`} style={{ background: s.color }} />
          {s.label} <span style={{ color: BRAND.muted }}>{counts?.[k] ?? 0}</span>
        </span>
      ))}
    </div>
  );
}

function BedTile({ bed, onClick, iso }) {
  const s = BED_STATUS[bed.status] || BED_STATUS.vacant;
  const first = bed.guest?.name?.split(" ")[0];
  return (
    <button data-testid={`bed-tile-${bed.label}`} onClick={() => onClick(bed)}
      className={`bed-tile relative rounded-xl text-left text-white font-bold ${s.blink ? "bed-blink" : ""} ${iso ? "bed-iso" : ""}`}
      style={{ background: s.color, "--bed-shade": s.color }}>
      <div className="text-[11px] leading-tight px-2 pt-1.5 opacity-95">{bed.label} <span className="font-normal opacity-80">{BED_TYPE[bed.bed_type]?.split(" ")[0] || ""}</span></div>
      <div className="text-[10px] px-2 truncate" data-testid={`bed-guest-${bed.label}`}>{first || s.label}</div>
      {bed.guest && !iso && <div className="text-[9px] px-2 pb-1.5 truncate opacity-90" data-testid={`bed-guest-meta-${bed.label}`}>{bed.guest.city || ""}{bed.guest.check_in ? ` · ${bed.guest.check_in.slice(5, 10)}` : ""}</div>}
      {!bed.guest && <div className="pb-1.5" />}
    </button>
  );
}

function RoomBlock({ room, iso, onBed, onDelete }) {
  return (
    <div className="rounded-2xl border-2 bg-white p-3" style={{ borderColor: BRAND.line }} data-testid={`room-${room.name}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>{room.name} <span className="text-[10px] font-normal" style={{ color: BRAND.muted }}>· {ROOM_TYPE[room.room_type] || "Room"}{room.ac ? " · AC" : room.room_type ? " · Non-AC" : ""} · Floor {room.floor}</span></div>
        {onDelete && (
          <button data-testid={`room-delete-${room.name}`} onClick={() => onDelete(room)} className="p-1 rounded-full hover:bg-red-50" aria-label="Delete room">
            <Trash2 className="w-3.5 h-3.5 text-red-600" />
          </button>
        )}
      </div>
      <div className={iso ? "iso-plane" : ""}>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {room.beds.map((b) => <BedTile key={b.id} bed={b} iso={iso} onClick={onBed} />)}
        </div>
      </div>
    </div>
  );
}

function AddRoomForm({ onAdd }) {
  const [f, setF] = useState({ name: "", floor: 0, beds_count: 4, nightly_rate: 400, monthly_rate: 6000, room_type: "dormitory", ac: false, bunk: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onAdd({ ...f, ac: !!f.ac, bunk: !!f.bunk, floor: +f.floor, beds_count: +f.beds_count, nightly_rate: +f.nightly_rate, monthly_rate: +f.monthly_rate })
      .then((ok) => ok && setF({ ...f, name: "" }));
  };
  const cls = "rounded-xl border-2 px-2.5 py-2 text-sm w-full";
  return (
    <form onSubmit={submit} data-testid="add-room-form" className="rounded-2xl border-2 border-dashed p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end" style={{ borderColor: `${BRAND.green}55` }}>
      <input required placeholder="Room (D1)" data-testid="room-name-input" className={`${cls} col-span-2 sm:col-span-1`} value={f.name} onChange={set("name")} />
      <input type="number" placeholder="Floor" data-testid="room-floor-input" className={cls} value={f.floor} onChange={set("floor")} />
      <input type="number" min={1} max={20} placeholder="Beds" data-testid="room-beds-input" className={cls} value={f.beds_count} onChange={set("beds_count")} />
      <input type="number" placeholder="₹/night" data-testid="room-nightly-input" className={cls} value={f.nightly_rate} onChange={set("nightly_rate")} />
      <input type="number" placeholder="₹/month" data-testid="room-monthly-input" className={cls} value={f.monthly_rate} onChange={set("monthly_rate")} />
      <select data-testid="room-type-select" className={cls} value={f.room_type} onChange={set("room_type")}>
        {Object.entries(ROOM_TYPE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <label className="flex items-center gap-1.5 text-xs font-bold" style={{ color: BRAND.green }}><input type="checkbox" data-testid="room-ac-checkbox" checked={f.ac} onChange={(e) => setF({ ...f, ac: e.target.checked })} /> AC</label>
      {f.room_type === "dormitory" && <label className="flex items-center gap-1.5 text-xs font-bold" style={{ color: BRAND.green }}><input type="checkbox" data-testid="room-bunk-checkbox" checked={f.bunk} onChange={(e) => setF({ ...f, bunk: e.target.checked })} /> Bunk beds (L/U)</label>}
      <button data-testid="add-room-submit" className="col-span-2 sm:col-span-1 rounded-full py-2 text-sm font-bold text-white inline-flex items-center justify-center gap-1" style={{ background: BRAND.green }}>
        <Plus className="w-4 h-4" /> Add room
      </button>
    </form>
  );
}

export function BedBoard({ pid, authFetch, API }) {
  const [data, setData] = useState(null);
  const [iso, setIso] = useState(false);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/board` })
    .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, "Could not load bed board"))), [pid, authFetch, API]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const addRoom = async (body) => {
    try { await authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/rooms`, data: body }); toast.success("Room added"); load(); return true; }
    catch (e) { toast.error(errMsg(e, "Could not add room")); return false; }
  };
  const delRoom = async (room) => {
    if (!window.confirm(`Delete ${room.name} and its beds?`)) return;
    try { await authFetch({ method: "delete", url: `${API}/homestay/rooms/${room.id}` }); toast.success("Room deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not delete room")); }
  };

  if (!data) return <div className="text-sm" style={{ color: BRAND.muted }} data-testid="bed-board-loading">Loading bed board…</div>;
  const isOwner = data.property.my_role === "owner";

  return (
    <div data-testid="bed-board" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Legend counts={data.counts} />
        <div className="inline-flex rounded-full border-2 p-1 bg-white" style={{ borderColor: BRAND.line }}>
          {[[false, "2D", Grid2x2], [true, "3D", Box]].map(([v, l, I]) => (
            <button key={l} data-testid={`view-toggle-${l.toLowerCase()}`} onClick={() => setIso(v)}
              className="px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 transition-colors"
              style={{ background: iso === v ? BRAND.green : "transparent", color: iso === v ? "#fff" : BRAND.green }}>
              <I className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
      </div>
      <div className="text-xs" style={{ color: BRAND.muted }} data-testid="bed-total">{data.total_beds}/50 beds used · auto-refreshes every 20s</div>
      {data.rooms.length === 0 && <div className="rounded-2xl p-6 text-center text-sm bg-white border" style={{ borderColor: BRAND.line, color: BRAND.muted }} data-testid="bed-board-empty">No rooms yet — add your first room below.</div>}
      <div className={`grid gap-4 ${iso ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
        {data.rooms.map((rm) => <RoomBlock key={rm.id} room={rm} iso={iso} onBed={setSel} onDelete={isOwner ? delRoom : null} />)}
      </div>
      {isOwner && <AddRoomForm onAdd={addRoom} />}
      <BedSheet bed={sel} onClose={() => setSel(null)} authFetch={authFetch} API={API} onSaved={() => { setSel(null); load(); }} />
    </div>
  );
}
